import React, { useMemo, useState, createContext, useContext, PropsWithChildren } from 'react'
import { useLocation } from 'react-router';
import im from 'immer'
import { ListFieldConfig } from 'src/components/DNA'
import { Variant, DocumentORM, DocumentVersionORM } from 'src/types/types'
import { SortOptions, FilterAndSortOptions, multiTypeSort } from 'src/state/redux/selector/common'
import { useUserTenant } from 'src/state/redux/selector/user'
// [TODO] - Move this implementation out of GridList
import { getFieldConfigsForTenant } from 'src/components/DNA/GridList/util'
import { useAppSettings } from 'src/state/context/AppSettings'
import ROUTES from 'src/router/routeDef';

export enum SortDir {
  asc = 'asc',
  desc = 'desc',
  none = 'none',
}

export enum SortFieldType {
  ver_model = 'ver_model',
  doc_model = 'doc_model',
  label = 'label'
}

export type SortEntry = {
  id: string,
  dir: SortDir | keyof typeof SortDir,
  fieldType: SortFieldType
  fieldResolver?: (docVersion: DocumentVersionORM) => any,
  key: string,
  label: string,
  orderByValue?: boolean,
}

// [TODO]: Make this a generic to have typechecking on keys?
export type SortMap = {
  [key: string]: SortEntry
}

export interface SortContext {
  sorts: SortMap,
  activeSort?: SortEntry,
  sortSelectorOpts: FilterAndSortOptions<DocumentORM>,
  setSorts: (state: SortMap) => void,
  toggleSort: (targetKey: string) => void
}

export const getSortConfigForFields = (fieldConfigs: ListFieldConfig[], ignoreDefaultSort?: boolean): SortMap => {
  const initSort: SortMap = {}
  fieldConfigs.filter(field => field).forEach((field) => {
    if (field.fieldType === 'LABEL' || field.fieldType === 'MODEL' || field.fieldType === 'DOC_MODEL') {
      const sortConfig: SortEntry = {
        id: field.id ?? field.fieldName,
        key: field.fieldName,
        fieldResolver: field.fieldResolver,
        fieldType: field.fieldType === 'LABEL' ? SortFieldType.label
          : field.fieldType === 'MODEL' ? SortFieldType.ver_model : SortFieldType.doc_model,
        label: field.fieldLabel,
        dir: !ignoreDefaultSort && field.defaultSort ? field.defaultSort : SortDir.none,
        // when we sort a custom field that represents a date, we want to sort by value
        // the value that the user see in the screen is the formatted date is not saved on the iso format
        orderByValue: field.dataType === 'date',
      }

      initSort[field.fieldName] = sortConfig
    }
  })
  return initSort;
}

const nextSortDir = {
  [SortDir.asc]: SortDir.desc,
  [SortDir.desc]: SortDir.asc,
  [SortDir.none]: SortDir.asc,
}

const SortContext = createContext<SortContext>({
  sorts: {},
  activeSort: undefined,
  sortSelectorOpts: {},
  setSorts: () => { },
  toggleSort: () => { },
})
SortContext.displayName = 'UseSortContext'

const SortContextProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const userTenant = useUserTenant()
  const { deviceMode } = useAppSettings()
  const isDesktop = deviceMode === 'desktop';
  const { pathname } = useLocation();
  const isSearchView = pathname.toLowerCase() === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH ||
      pathname.toLowerCase().startsWith('/search');
  const isBookmarkView = pathname.toLowerCase() === ROUTES.UI2_FAVORITE_FILES.PATH.toLowerCase() ||
      pathname.toLowerCase().startsWith('/favorites/files');
  const variant = isBookmarkView ? Variant.bookmark : Variant.MSL;
  const fieldConfigs = getFieldConfigsForTenant(userTenant, variant, isDesktop)

  // IF IT'S SEARCH VIEW, WE'LL IGNORE THE DEFAULT SORT CONFIG
  const [sorts, setSorts] = useState<SortMap>(getSortConfigForFields(fieldConfigs, isSearchView))

  const activeSort = useMemo(() => {
    let activeSort: (SortEntry | undefined)

    for (const sort in sorts) {
      if (sorts[sort].dir !== SortDir.none) {
        activeSort = sorts[sort]
        break; // Only returns the first active sort found
      }
    }

    return activeSort
  }, [sorts])

  const toggleSort = useMemo(
    () => {
      return (targetKey: string) => {
        setSorts(p => im(p, sortMap => {
          for (const sortKey in sortMap) {
            if (sortMap[sortKey].key === targetKey) {
              const currDir = sortMap[sortKey].dir
              const nextDir = nextSortDir[currDir]

              sortMap[sortKey] = {
                ...sortMap[sortKey],
                dir: nextDir,
                orderByValue: !!sortMap[sortKey].orderByValue,
              }
            } else {
              sortMap[sortKey].dir = SortDir.none
            }
          }
        }))
      }
    },
    [sorts],
  )

  // Selector filter
  const sortSelectorOpts = useMemo(() => {
    let sort: SortOptions<DocumentORM>[] = []

    if (activeSort) {
      switch (activeSort.fieldType) {
        case SortFieldType.label:
          sort = [{
            meta: {
              customValues: (a, b) => {
                const sortByValue = activeSort.orderByValue || false

                return multiTypeSort(
                  sortByValue ? a.configsMap![activeSort.id]?.values : a.configsMap![activeSort.id]?.displayValues,
                  sortByValue ? b.configsMap![activeSort.id]?.values : b.configsMap![activeSort.id]?.displayValues,
                  { dir: activeSort.dir } as any,
                )
              },
            },
          }]
          break
        case SortFieldType.doc_model:
          sort = [{ model: { [activeSort.key]: activeSort.dir } }]
          break
        case SortFieldType.ver_model:
          sort = [{
            relations: {
              version: (a, b) => {
                if (activeSort.fieldResolver) {
                  return multiTypeSort(
                    activeSort.fieldResolver(a.latestDocumentVersionORM),
                    activeSort.fieldResolver(b.latestDocumentVersionORM),
                    { dir: activeSort.dir } as any,
                  )
                }

                return multiTypeSort(
                  a.latestUsableDocumentVersionORM.model[activeSort.key],
                  b.latestUsableDocumentVersionORM.model[activeSort.key],
                  { dir: activeSort.dir } as any,
                )
              },
            },
          }]
          break
      }
    }

    return { sort }
  }, [sorts])

  const value = {
    sorts,
    activeSort,
    sortSelectorOpts,
    setSorts,
    toggleSort,
  }

  return (
    <SortContext.Provider value={value}>
      {children}
    </SortContext.Provider>
  )
}

export const withSort = <T extends object = {}>(Component) => (props: T) => (
  <SortContextProvider>
    <Component {...props as T} />
  </SortContextProvider>
)

export const useSort = () => useContext(SortContext)
